import React, { useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { IconContext } from 'react-icons';
import { useTranslation } from 'react-i18next';
import { IconWrapper } from '../ui/IconWrapper';
import { ProjectSelector } from './ProjectSelector';
import matrixAILogo from '../../assets/MatrixAILogo.png';
import { 
  RiDashboardLine,
  RiBuilding4Line, 
  RiShieldCheckLine,
  RiGroupLine,
  RiBrushLine,
  RiFileUserLine,
  RiSettings3Line,
  RiArrowRightSLine,
  RiCloseLine,
  RiBrainLine
} from 'react-icons/ri';
import { useProjects } from '../../contexts/ProjectContext';
import { useFormCounts } from '../../contexts/FormCountsContext';

// Logo component
const Logo: React.FC<{ collapsed: boolean }> = ({ collapsed }) => {
  return (
    <div className="flex items-center justify-center pt-3 pb-2">
      {collapsed ? (
        <motion.div 
          className="h-9 w-9 rounded-lg flex items-center justify-center relative"
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
        >
          <div className="absolute inset-0 overflow-hidden"></div>
          <img src={matrixAILogo} alt="MatrixAI Logo" className="h-10 w-10 object-contain relative z-10" />
        </motion.div>
      ) : (
        <div className="flex items-center">
          <motion.div 
            className="h-10 w-10 rounded-xl flex items-center justify-center relative mr-3"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <div className="absolute inset-0  overflow-hidden"></div>
            <img src={matrixAILogo} alt="MatrixAI Logo" className="h-10 w-10 object-contain relative z-10" />
          </motion.div>
          <div className="font-display font-bold text-xl leading-none h-10 flex items-center">
            <span className="text-white">
              Matrix<span className="text-portfolio-orange">Twin</span>
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

interface NavItemProps {
  to: string;
  icon: React.ReactNode;
  label: string;
  collapsed: boolean;
  badgeCount?: number;
  mobile?: boolean;
  onClick?: () => void;
  end?: boolean;
}

interface SidebarItem {
  to: string;
  icon: React.ReactNode;
  label: string;
  badgeCount?: number;
  end?: boolean;
}

const NavItem: React.FC<NavItemProps> = ({ to, icon, label, collapsed, badgeCount = 0, mobile, onClick, end }) => {
  const { t } = useTranslation();
  const { selectedProject } = useProjects();
  const [isPressed, setIsPressed] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  // Add project ID to navigation if a project is selected
  const getNavigationPath = () => {
    if (selectedProject && to !== '/projects') {
      if (to === '/dashboard') {
        return `/dashboard/${selectedProject.id}`;
      }

      const projectScopedRoutes = [
        '/tasks', '/team', '/create-role', '/rfi', '/diary', 
        '/safety', '/labour', '/cleansing', '/forms', '/reports', 
        '/analytics', '/settings', '/voice-call', '/digital-twins', '/ask-ai'
      ];

      const isProjectScoped = projectScopedRoutes.some(route => to.startsWith(route));

      if (isProjectScoped) {
        return `/dashboard/${selectedProject.id}${to}`;
      }
    }
    return to;
  };

  const handleItemClick = () => {
    setIsPressed(true);
    setTimeout(() => setIsPressed(false), 200);
    if (onClick) onClick();
  };

  return (
    <NavLink
      to={getNavigationPath()}
      end={end}
      onClick={handleItemClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={({ isActive }) =>
        `flex items-center px-4 py-3 relative ${
          isActive 
          ? 'text-white font-medium bg-portfolio-orange/10 border-l-2 border-portfolio-orange' 
          : 'text-gray-400 dark:text-gray-300 hover:text-white hover:bg-white/5 border-l-2 border-transparent'
        } ${
          collapsed && !mobile ? 'justify-center' : 'justify-between'
        } rounded-lg transition-all duration-300 ${
          mobile ? 'py-4' : ''
        } ${
          isPressed ? 'scale-95' : ''
        }`
      }
    >
      {({ isActive }) => (
        <>
          {isActive && (
            <motion.div
              className="absolute inset-0 rounded-lg opacity-30 z-0 overflow-hidden"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
            >
              <div className="absolute inset-0 bg-portfolio-orange/5" />
            </motion.div>
          )}
          <div className="flex items-center relative z-10">
            <motion.div
              animate={{ 
                scale: isActive || isHovered ? 1.1 : 1,
                color: isActive ? '#FF4D00' : isHovered ? '#FFFFFF' : 'rgb(156, 163, 175)'
              }}
              transition={{ duration: 0.2 }}
              className={`${isActive ? 'text-portfolio-orange' : ''}`}
            >
              <IconContext.Provider value={{ size: '22px' }}>
                {icon}
              </IconContext.Provider>
            </motion.div>
            {(!collapsed || mobile) && (
              <motion.span 
                className={`ml-3 font-medium relative whitespace-nowrap ${isActive ? 'text-white' : ''}`}
                animate={{ 
                  x: isActive ? 2 : 0,
                }}
                transition={{ duration: 0.2 }}
              >
                {t(label)}
                {isActive && (
                  <motion.span 
                    className="absolute -bottom-1 left-0 right-0 h-[2px] bg-gradient-to-r from-portfolio-orange to-orange-600"
                    layoutId="underline"
                  />
                )}
              </motion.span>
            )}
          </div>
          
          {badgeCount > 0 && (!collapsed || mobile) && (
            <span className="bg-gradient-to-r from-portfolio-orange to-orange-600 text-white text-xs font-semibold w-6 h-6 flex items-center justify-center rounded-full absolute right-2 top-1/2 -translate-y-1/2 z-10 shadow-[0_0_10px_rgba(255,87,34,0.5)]">
              {badgeCount}
            </span>
          )}
        </>
      )}
    </NavLink>
  );
};

interface SidebarProps {
  mobile?: boolean;
  onClose?: () => void;
  onCollapseChange?: (collapsed: boolean) => void;
}

export const Sidebar: React.FC<SidebarProps> = ({ mobile = false, onClose, onCollapseChange }) => {
  const [collapsed, setCollapsed] = useState(false);
  const { t } = useTranslation();
  const { diaryCount, safetyCount, labourCount, cleansingCount, rfiCount, formsCount } = useFormCounts();
  
  // Always expanded on mobile
  useEffect(() => {
    if (mobile) {
      setCollapsed(false);
    }
  }, [mobile]);
  
  // Notify parent component when collapsed state changes
  useEffect(() => {
    if (onCollapseChange && !mobile) {
      onCollapseChange(collapsed);
    }
  }, [collapsed, onCollapseChange, mobile]);
  
  // Primary nav items
  const primaryNavItems: SidebarItem[] = [
    { 
      to: '/dashboard', 
      icon: (
        <div><IconWrapper icon="RiDashboardLine" className="text-xl" /></div>
      ), 
      label: 'nav.dashboard',
      end: true
    },
    { 
      to: '/digital-twins/viewer', 
      icon: (
        <div><IconWrapper icon="RiBuilding4Line" className="text-xl" /></div>
      ), 
      label: 'nav.digitalTwins'
    },
    { 
      to: '/ask-ai', 
      icon: (
        <div><IconWrapper icon="RiBrainLine" className="text-xl" /></div>
      ), 
      label: 'nav.askAI',
      badgeCount: 0
    },
  ];
  
  // DWSS Modules
  const dwssModules: SidebarItem[] = [
    { 
      to: '/rfi', 
      icon: (
        <div><IconWrapper icon="RiFileList3Line" className="text-xl" /></div>
      ), 
      label: 'nav.forms',
      badgeCount: rfiCount
    },
    { 
      to: '/diary', 
      icon: (
        <div><IconWrapper icon="RiCalendarCheckLine" className="text-xl" /></div>
      ), 
      label: 'diary.title',
      badgeCount: diaryCount
    },
    { 
      to: '/safety', 
      icon: (
        <div><IconWrapper icon="RiShieldCheckLine" className="text-xl" /></div>
      ), 
      label: 'safety.title',
      badgeCount: safetyCount
    },
    { 
      to: '/labour', 
      icon: (
        <div><IconWrapper icon="RiGroupLine" className="text-xl" /></div>
      ), 
      label: 'labour.title',
      badgeCount: labourCount
    },
    { 
      to: '/cleansing', 
      icon: (
        <div><IconWrapper icon="RiBrushLine" className="text-xl" /></div>
      ), 
      label: 'cleansing.title',
      badgeCount: cleansingCount
    },
    { 
      to: '/forms', 
      icon: (
        <div><IconWrapper icon="RiFileUserLine" className="text-xl" /></div>
      ), 
      label: 'nav.customForms',
      badgeCount: formsCount
    }
  ];
  
  // Management nav items
  const managementItems: SidebarItem[] = [
   { 
      to: '/team', 
      icon: (
        <div><IconWrapper icon="RiTeamLine" className="text-xl" /></div>
      ), 
      label: 'team.title'
    },
  
    { 
      to: '/settings', 
      icon: (
        <div><IconWrapper icon="RiSettings3Line" className="text-xl" /></div>
      ), 
      label: 'settings.title'
    }
  ];
  
  // For the beautiful glow effect based on scroll
  const [scrollPosition, setScrollPosition] = useState(0);
  
  useEffect(() => {
    const handleScroll = (e: any) => {
      if (e.target) {
        setScrollPosition(e.target.scrollTop);
      }
    };
    
    const sidebar = document.getElementById('sidebar-content');
    if (sidebar) {
      sidebar.addEventListener('scroll', handleScroll);
      return () => sidebar.removeEventListener('scroll', handleScroll);
    }
  }, []);

  // Toggle collapsed state
  const toggleCollapsed = () => {
    setCollapsed(!collapsed);
  };

  const handleNavItemClick = () => {
    if (mobile && onClose) {
      onClose();
    }
  };
  
  return (
    <motion.aside 
      className={`
        fixed top-0 left-0 h-screen z-50
        bg-portfolio-dark border-r border-white/5 shadow-xl flex flex-col overflow-visible
        transition-all duration-300 ease-in-out 
        ${mobile ? 'w-64' : collapsed ? 'w-16' : 'w-64'}
      `}
      layout
    >
      {mobile && (
        <motion.button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-full bg-dark-800 hover:bg-dark-700 text-gray-400 hover:text-white"
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
        >
          <div><IconWrapper icon="RiCloseLine" className="text-xl" /></div>
        </motion.button>
      )}

      {!mobile && (
        <div className="absolute left-full top-1/2 -translate-y-1/2 -ml-4 z-50">
          <motion.button
            onClick={toggleCollapsed}
            className="h-14 w-8 flex items-center justify-center rounded-xl border border-white/20 bg-white/10 backdrop-blur-md text-white shadow-[0_10px_24px_rgba(0,0,0,0.35)] hover:bg-white/20 transition-colors duration-200"
            whileHover={{ scale: 1.08 }}
            whileTap={{ scale: 0.94 }}
          >
            <motion.div
              animate={{
                rotate: collapsed ? 0 : 180
              }}
              transition={{ duration: 0.3 }}
            >
              <div><IconWrapper icon="RiArrowRightSLine" className="text-xl" /></div>
            </motion.div>
          </motion.button>
        </div>
      )}

      <div className="relative overflow-hidden h-full flex flex-col">
        {/* Beautiful gradient backdrop */}
        <div className="absolute top-0 left-0 right-0 h-64 bg-gradient-to-b from-portfolio-orange/5 to-transparent pointer-events-none" />
        
        <div className="absolute bottom-0 right-0 w-full h-64 bg-gradient-to-t from-portfolio-orange/5 to-transparent pointer-events-none" />
        
        {/* Moving gradient based on scroll */}
        <motion.div 
          className="absolute inset-0 pointer-events-none opacity-40"
          style={{
            background: `radial-gradient(circle at ${collapsed && !mobile ? '50%' : '70%'} ${30 + scrollPosition/10}%, rgba(63, 135, 255, 0.15) 0%, transparent 50%)`,
          }}
        />
        
        <Logo collapsed={collapsed && !mobile} />
        
        {/* Project Selector */}
        <ProjectSelector collapsed={collapsed && !mobile} mobile={mobile} />
        
        <div 
          id="sidebar-content"
          className="flex-1 pt-6 pb-4 px-2 overflow-y-auto overflow-x-hidden scrollbar-hide"
        >
          <AnimatePresence>
            <motion.nav layout className="space-y-6">
              {/* Main Navigation */}
              <div>
                {(!collapsed || mobile) && (
                  <h3 className="text-xs uppercase text-gray-500 font-medium px-4 mb-2">
                    {t('common.main', 'Project Management')}
                  </h3>
                )}
                {primaryNavItems.map((item, index) => (
                  <motion.div
                    key={item.to}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <NavItem
                      to={item.to}
                      icon={item.icon}
                      label={item.label}
                      collapsed={collapsed && !mobile}
                      badgeCount={item.badgeCount}
                      mobile={mobile}
                      onClick={handleNavItemClick}
                      end={item.end}
                    />
                  </motion.div>
                ))}
              </div>
              
              {/* DWSS Modules */}
              <div>
                {(!collapsed || mobile) && (
                  <h3 className="text-xs uppercase text-gray-500 font-medium px-4 mb-2">
                    {t('common.modules', 'ISO19650 DWSS')}
                  </h3>
                )}
                {dwssModules.map((item, index) => (
                  <motion.div
                    key={item.to}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ delay: 0.2 + index * 0.05 }}
                  >
                    <NavItem
                      to={item.to}
                      icon={item.icon}
                      label={item.label}
                      collapsed={collapsed && !mobile}
                      badgeCount={item.badgeCount}
                      mobile={mobile}
                      onClick={handleNavItemClick}
                      end={item.end}
                    />
                  </motion.div>
                ))}
              </div>
              
              {/* Management */}
              <div>
                {(!collapsed || mobile) && (
                  <h3 className="text-xs uppercase text-gray-500 font-medium px-4 mb-2">
                    {t('common.management', 'Management')}
                  </h3>
                )}
                {managementItems.map((item, index) => (
                  <motion.div
                    key={item.to}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ delay: 0.4 + index * 0.05 }}
                  >
                    <NavItem
                      to={item.to}
                      icon={item.icon}
                      label={item.label}
                      collapsed={collapsed && !mobile}
                      badgeCount={item.badgeCount}
                      mobile={mobile}
                      onClick={handleNavItemClick}
                      end={item.end}
                    />
                  </motion.div>
                ))}
              </div>
            </motion.nav>
          </AnimatePresence>
        </div>
      </div>
    </motion.aside>
  );
}; 
